import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import {
  Search,
  Select,
  SelectItem,
  Pagination,
} from 'carbon-components-react';
import queryString from 'query-string';
import { formatDate } from '../../../utils/common.util';
import LoaderPane from '../../../components/loader-pane/loader-pane.component';
import { useHistory, useLocation } from 'react-router-dom';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;

const getFetchBillingStateFromSearch = (search = '') => {
  const params = queryString.parse(search);
  const page = parseInt(params.page, 10);
  const pageSize = parseInt(params.pageSize, 10);

  return {
    query: params.query || '',
    status: params.status || '',
    paginate: {
      page: Number.isNaN(page) ? DEFAULT_PAGE : page,
      pageSize: Number.isNaN(pageSize) ? DEFAULT_PAGE_SIZE : pageSize,
    },
  };
};

const getFetchBillingSearch = (state) =>
  queryString.stringify(
    {
      query: state.query,
      status: state.status,
      page: state.paginate.page,
      pageSize: state.paginate.pageSize,
    },
    { skipEmptyString: true, skipNull: true },
  );

const FetchBillings = ({ onViewBilling, onSetBillings, onSetBillingFees }) => {
  const location = useLocation();
  const history = useHistory();

  const initialState = getFetchBillingStateFromSearch(location.search);

  const [state, setState] = useState(initialState);
  const [draftQuery, setDraftQuery] = useState(initialState.query);

  useEffect(() => {
    setState((prev) => {
      const nextState = getFetchBillingStateFromSearch(location.search);

      if (
        nextState.query === prev.query &&
        nextState.status === prev.status &&
        nextState.paginate.page === prev.paginate.page &&
        nextState.paginate.pageSize === prev.paginate.pageSize
      ) {
        return prev;
      }

      return nextState;
    });
    setDraftQuery(getFetchBillingStateFromSearch(location.search).query);
  }, [location.search]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setState((prev) => {
        if (prev.query === draftQuery) {
          return prev;
        }

        return {
          ...prev,
          query: draftQuery,
          paginate: { ...prev.paginate, page: 1 },
        };
      });
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [draftQuery]);

  useEffect(() => {
    const search = getFetchBillingSearch(state);
    const nextSearch = search.length > 0 ? `?${search}` : '';

    if (nextSearch !== location.search) {
      history.replace({
        pathname: location.pathname,
        search: nextSearch,
      });
    }
  }, [history, location.pathname, location.search, state]);

  const { data, error, mutate } = useSWR(
    `billing/list?skip=${state.paginate.pageSize * (state.paginate.page - 1)}&take=${
      state.paginate.pageSize
    }&query=${state?.query}&status=${state?.status}`,
    {
      onSuccess: (d) => {
        //console.log(d);
        if (d?.length > 0) onSetBillings(d);
      },
    },
  );

  useSWR(data && `billing/fees/?classId=${-1}&includeGeneralFees=${false}`, {
    onSuccess: (d) => {
      //console.log("\n billing fees", d);
      if (d) onSetBillingFees(d);
    },
  });

  return (
    <div className="mt-30">
      <div className="bx--row mb-30">
        <div className="bx--col-lg-3">
          <Select
            noLabel
            value={state.status}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                status: e.target.value,
                paginate: { ...prev.paginate, page: 1 },
              }))
            }
          >
            <SelectItem
              disabled
              selected
              value="Filter by Status"
              text="Filter by Status"
            />

            <SelectItem value="" text="All" />
            <SelectItem value="Blank" text="Blank" />
            <SelectItem value="Generated" text="Generated" />
            <SelectItem value="Voided" text="Voided" />
          </Select>
        </div>
        <div className="bx--col-lg-9">
          <Search
            id="search"
            name="search"
            labelText=""
            value={draftQuery}
            onChange={(event) => setDraftQuery(event?.target?.value || '')}
            placeHolderText="Search by Class, Academic Year or Term"
          />
        </div>
      </div>

      <LoaderPane
        loading={!data && !error}
        error={error?.message}
        noRecords={!error && data?.length === 0}
        onReload={() => {
          mutate();
        }}
      />

      {data?.length > 0 && (
        <div className="table-container">
          <table className="bx--data-table mt-10 table-white">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date Billed</th>
                <th>Academic Year</th>
                <th>Term</th>
                <th>Class</th>
                <th>Total Bills</th>
                {/*<th>Total Amount Billed</th>*/}
                <th>Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((billing) => (
                <tr>
                  <td>{billing.uniqueId}</td>
                  <td>{formatDate(billing.dateCreated)}</td>
                  <td>{billing.academicYear.description}</td>
                  <td>{billing.term.description}</td>
                  <td>{billing.class.description}</td>
                  <td>{billing.totalBillsCount}</td>
                  {/*<td>N/A</td>*/}
                  <td className={billing.status.toLowerCase()}>
                    {billing.status.toUpperCase()}
                  </td>
                  <td className="text-center">
                    <span
                      className="link"
                      onClick={() => {
                        onViewBilling(billing);
                        //props.history.push(`/billing/${billing.id}`);
                      }}
                    >
                      VIEW
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination
        backwardText="Previous page"
        forwardText="Next page"
        page={state.paginate.page}
        pageNumberText="Page Number"
        pageSize={state.paginate.pageSize}
        pageSizes={[10, 20, 30, 40, 50, 100]}
        totalItems={1000}
        onChange={(value) => {
          setState((prev) => ({ ...prev, paginate: value }));
        }}
      />
    </div>
  );
};

export default FetchBillings;
